/**
 * Schedule Invariants Checker
 * 
 * Validates critical scheduling rules after any placement/recalculation.
 * Violations indicate bugs in scheduling logic.
 */

import { clock } from './clock';
import { parseISO } from 'date-fns';
// DMC-D3c-2: canonical deleted-state helper. Mirrors public.is_ticket_deleted
// (SQL) from migration 20260508120000_dmc_d2_deleted_state_helpers.sql.
import { isTicketDeleted } from './ticketDeletedState';
// DMC-E: canonical parking-lot helper. Mirrors `tickets.parking_lot = true`
// (SoT). The legacy `status='Parking Lot'` representation is rejected by
// the DB CHECK constraint added in migration 20260424000000 and is dead
// in the codebase; this helper centralizes the canonical check.
import { isInParkingLot } from './parkingLotState';
// DTC-C: thread shop-tz minute computation through `getMinutesInShopTZ`
// instead of `Date#getHours()`. The latter returns BROWSER-local hours
// for a UTC `task.scheduled_start` and silently produces wrong shift-
// boundary comparisons when the operator's browser TZ differs from
// the shop's. The canonical helper accepts a UTC ISO + shop TZ string
// and returns minutes-since-midnight in the shop's local frame.
import { getMinutesInShopTZ } from './timezoneMath';
// DTC-F: route the past-start-time invariant's shop-today derivation
// through the canonical `getTodayInShopTz()` helper. The pre-DTC-F
// `new Date().toLocaleDateString('en-CA', { timeZone })` pattern
// silently bypassed the canonical clock — same drift class DTC-D
// fixed in `cleanup-stale-tickets`. Behavior identical at production
// wall-clock time; freezable under FIXED_TIME / setFixed fixtures.
//
// CSI-S2: extend the import to include `getTechDayHours` + `getShopDayLabel`.
// SPECIALTY_TECH_SHIFT now reads canonical per-day shift bounds via the
// hours envelope when shopTimezone is available, falling back to the
// deprecated flat columns only when shopTimezone is missing (legacy
// caller path — same dual-path structure as DTC-C).
import { getTodayInShopTz, getTechDayHours, getShopDayLabel } from './shopTime';
// CORE-BQ Unit 4: the same enum->label mapping the canonical
// `sync_tech_bay_assignments` writer applies when it rebuilds
// `technicians.bay_assignments`. Verified against the live function
// definition for all eleven bay_type values.
import { bayTypeToLabel } from './bayTypeLabels';
// CORE-BQ Unit 1: the audit reports what it actually checked. The recorder wraps
// each check so a skipped, degraded or throwing rule cannot look like a clean
// one; it changes no rule's logic and no rule's output.
import {
  createInvariantRecorder,
  requiredFieldsFor,
  missingFieldsIn,
  type InvariantCoverage,
  type InvariantVerdict,
  type InvariantRuleId,
  type InvariantDataset,
  type InvariantGate,
  type InvariantMissingFields,
  type InvariantInputRowScope,
} from './invariantCoverage';

/**
 * CORE-BQ Unit 3: does each rule actually have the COLUMNS it reads?
 *
 * A dataset can arrive loaded while the caller's projection omitted a column
 * the rule reasons over, and an omitted key is indistinguishable from a NULL
 * value once the rows are in memory. Measured on the sole production
 * projection before this existed: a real overlap became invisible while
 * NO_OVERLAPS reported CHECKED, a legal rollover child was reported as a
 * violation while PARKING_LOT_IMMUNITY reported CHECKED, and a correctly
 * synced technician was reported while BAY_SYNC reported CHECKED.
 *
 * Composed with the dataset gate rather than replacing it: a plane that was
 * never loaded has no columns to be missing, so `missing` still wins.
 */
function buildGate(
  rules: readonly InvariantRuleId[],
  datasetGate: InvariantGate,
  planes: Readonly<Partial<Record<InvariantDataset, unknown>>>,
): InvariantGate {
  if (datasetGate.missing) return datasetGate;
  const required = requiredFieldsFor(rules);
  const missingFields: InvariantMissingFields[] = [];
  for (const [dataset, fields] of Object.entries(required) as [InvariantDataset, string[]][]) {
    const rows = planes[dataset] as Record<string, unknown>[] | null | undefined;
    const missing = missingFieldsIn(rows, fields);
    if (missing.length > 0) missingFields.push({ dataset, fields: missing });
  }
  return missingFields.length > 0 ? { ...datasetGate, missingFields } : datasetGate;
}

export interface ScheduleViolation {
  rule: string;
  severity: 'error' | 'warning';
  message: string;
  ticketId?: string;
  techId?: string;
  bayId?: string;
  details?: any;
}

/**
 * CORE-BQ responsibilities 7 and 8.
 *
 * `valid` is unchanged and still means exactly what it always meant: no
 * violation was found AMONG THE CHECKS THAT PRODUCED RESULTS. It is kept for
 * every existing caller, and it must not be read on its own as a statement
 * about the whole audit — that is the misreading this contract exists to make
 * impossible. `verdict` and `coverage` carry the rest of the truth.
 */
export interface ScheduleInvariantsResult {
  valid: boolean;
  /** PASS only when nothing was found AND everything intended was evaluated. */
  verdict: InvariantVerdict;
  violations: ScheduleViolation[];
  /** Rule by rule: what ran, what did not, and why. */
  coverage: InvariantCoverage;
}

/**
 * Main invariant checker - validates all scheduling rules
 */
/**
 * Main invariant checker - validates all scheduling rules
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * INPUT PRESENCE IS PART OF THE ANSWER
 * ═══════════════════════════════════════════════════════════════════════════
 * `bays`, `specialtyTasks` and `bayBookings` accept `null`/`undefined` to mean
 * "this dataset was not loaded", which is a different fact from `[]` meaning
 * "loaded, and the shop has none". Both used to arrive as `[]` and the result
 * could not tell them apart: the only production caller passed a literal `[]`
 * for bays it had never fetched, so BAY_TYPE_REQUIREMENT and BAY_SYNC examined
 * nothing and the audit still read `valid: true`.
 *
 * An empty array is still honoured as real operational truth — a shop with no
 * specialty tasks today gets those rules CHECKED against an empty state, and
 * can legitimately PASS.
 *
 * No rule's logic changed here. What changed is that skipping one is now
 * recorded instead of being invisible.
 */
export function checkScheduleInvariants(
  tickets: any[],
  technicians: any[],
  /** `null`/`undefined` = not loaded. `[]` = loaded, and the shop has none. */
  bays: any[] | null | undefined,
  shopProfile: any,
  /** `null`/`undefined` = not loaded. `[]` = loaded, and there are none. */
  specialtyTasks?: any[] | null,
  /** `null`/`undefined` = not loaded. `[]` = loaded, and there are none. */
  bayBookings?: any[] | null,
  /**
   * CORE-BQ Unit 6. What the caller claims about the POPULATIONS above.
   *
   * Optional for compatibility with 74 existing call sites, but omission is
   * NOT neutral — it resolves to UNKNOWN on every plane, and an UNKNOWN
   * population cannot produce a scope-complete audit. A caller earns PASS by
   * describing its query, never by staying silent about it.
   */
  rowScope?: InvariantInputRowScope | null
): ScheduleInvariantsResult {
  const audit = createInvariantRecorder(rowScope);

  // Which planes the caller actually supplied. `Array.isArray` rather than a
  // truthiness test, precisely so `[]` counts as loaded.
  const haveTickets = Array.isArray(tickets);
  const haveTechs = Array.isArray(technicians);
  const haveBays = Array.isArray(bays);
  const haveTasks = Array.isArray(specialtyTasks);
  const haveBookings = Array.isArray(bayBookings);
  // Both specialty planes are needed together: every S-rule reads one and most
  // read both, so one alone cannot evaluate the plane.
  const specialtyGate = !haveTasks
    ? { missing: 'specialty_tasks_not_loaded' as const }
    : !haveBookings
      ? { missing: 'bay_bookings_not_loaded' as const }
      : {};

  // The shop's timezone is not decoration for two of these rules. Without it
  // they fall back to the operator's BROWSER clock, which is a documented
  // legacy path that answers a different question than the shop-relative
  // invariant does. It runs, so its violations are real; it is not the intended
  // check, so it is not counted as complete coverage.
  const shopTz: string | undefined = shopProfile?.time_zone;
  const tzGate = shopTz ? {} : { degraded: 'shop_timezone_missing' as const };

  const noTickets = { missing: 'tickets_not_loaded' as const };
  const ticketGate = haveTickets ? {} : noTickets;

  // Every plane the field check may inspect, in one place.
  const planes = { tickets, technicians, bays, specialtyTasks, bayBookings } as
    Readonly<Partial<Record<InvariantDataset, unknown>>>;
  const gate = (rules: readonly InvariantRuleId[], datasetGate: InvariantGate) =>
    buildGate(rules, datasetGate, planes);

  // Rule 1: No overlaps per technician per bay
  audit.run(['NO_OVERLAPS'], gate(['NO_OVERLAPS'], ticketGate), () => checkNoOverlaps(tickets));

  // Rule 1B: No GLOBAL bay overlaps (same bay, different techs)
  audit.run(['BAY_GLOBAL_OVERLAP'], gate(['BAY_GLOBAL_OVERLAP'], ticketGate), () => checkGlobalBayOverlaps(tickets));

  // Rule 2: No start times in the past (shop-TZ aware stale classification)
  audit.run(['NO_PAST_TIMES'], gate(['NO_PAST_TIMES'], haveTickets ? tzGate : noTickets),
    () => checkNoPastStartTimes(tickets, shopTz));

  // Rule 3: Waiter EOD violations.
  // CSI-J1: thread shop timezone through so the rule compares
  // `opti_finish` (UTC ISO timestamptz) against the tech's per-day shift
  // end in a shop-local frame. Pre-CSI-J1 the rule was silently
  // double-broken — see `checkWaiterEOD` JSDoc for the audit.
  audit.run(['WAITER_EOD'], gate(['WAITER_EOD'],
    !haveTickets ? noTickets
      : !haveTechs ? { missing: 'technicians_not_loaded' as const } : tzGate),
    () => checkWaiterEOD(tickets, technicians, shopProfile, shopTz));

  // Rule 4: Parking Lot immunity (no auto-assigned completed tickets)
  audit.run(['PARKING_LOT_IMMUNITY'], gate(['PARKING_LOT_IMMUNITY'], ticketGate), () => checkParkingLotImmunity(tickets));

  // Rule 5: Bay type requirements (lift jobs can't be in flat bays)
  audit.run(['BAY_TYPE_REQUIREMENT'], gate(['BAY_TYPE_REQUIREMENT'],
    !haveTickets ? noTickets
      : !haveBays ? { missing: 'bays_not_loaded' as const } : {}),
    () => checkBayTypeRequirements(tickets, bays as any[]));

  // Rule 6: Bay assignment synchronization
  audit.run(['BAY_SYNC'], gate(['BAY_SYNC'],
    !haveTechs ? { missing: 'technicians_not_loaded' as const }
      : !haveBays ? { missing: 'bays_not_loaded' as const } : {}),
    () => checkBayAssignmentSync(technicians, bays as any[]));

  // Specialty Invariants. Previously wrapped in `if (specialtyTasks &&
  // bayBookings)`, which skipped nine rules in silence.
  const tasks = specialtyTasks as any[];
  const bookings = bayBookings as any[];

  // Rule S1: Specialty tasks must have valid bay bookings
  audit.run(['SPECIALTY_TASK_BOOKING'], gate(['SPECIALTY_TASK_BOOKING'], specialtyGate),
    () => checkSpecialtyTaskBookings(tasks, bookings));

  // Rule S2: Specialty bays cannot be double-booked
  audit.run(['SPECIALTY_BAY_DOUBLE_BOOKING'], gate(['SPECIALTY_BAY_DOUBLE_BOOKING'], specialtyGate),
    () => checkSpecialtyBayDoubleBooking(bookings));

  // Rule S3: Alignment/ADAS tasks must be in appropriate specialty bays
  audit.run(['SPECIALTY_BAY_TYPE'], gate(['SPECIALTY_BAY_TYPE'],
    specialtyGate.missing ? specialtyGate
      : !haveBays ? { missing: 'bays_not_loaded' as const } : {}),
    () => checkSpecialtyBayTypes(tasks, bays as any[]));

  // Rule S4: Specialty sub-tickets must reference valid primary tickets
  audit.run(['SPECIALTY_SUB_REFERENCE', 'SPECIALTY_PRIMARY_REFERENCE'],
    gate(['SPECIALTY_SUB_REFERENCE', 'SPECIALTY_PRIMARY_REFERENCE'],
      specialtyGate.missing ? specialtyGate
        : !haveTickets ? noTickets : {}),
    () => checkSpecialtySubTicketReferences(tasks, tickets));

  // Rule S5: Specialty tasks must have qualified technicians
  audit.run(['SPECIALTY_TECH_PROFICIENCY'], gate(['SPECIALTY_TECH_PROFICIENCY'],
    specialtyGate.missing ? specialtyGate
      : !haveTechs ? { missing: 'technicians_not_loaded' as const } : {}),
    () => checkSpecialtyTechProficiency(tasks, technicians, shopProfile));

  // Rule S6: Specialty task scheduling must respect technician shifts.
  // DTC-C: thread shop timezone so the task's UTC `scheduled_start` is
  // compared against `tech.shift_start` (shop-local HH:mm) using the
  // shop's wall clock — not the operator's browser clock.
  audit.run(['SPECIALTY_TECH_SHIFT'], gate(['SPECIALTY_TECH_SHIFT'],
    specialtyGate.missing ? specialtyGate
      : !haveTechs ? { missing: 'technicians_not_loaded' as const } : tzGate),
    () => checkSpecialtyTechShifts(tasks, technicians, shopTz));

  // Rule S7: Specialty bay bookings must not overlap
  audit.run(['SPECIALTY_BOOKING_OVERLAP'], gate(['SPECIALTY_BOOKING_OVERLAP'], specialtyGate),
    () => checkSpecialtyBookingOverlaps(bookings));

  // Rule S8: Specialty tasks must have valid status transitions
  audit.run(['SPECIALTY_STATUS'], gate(['SPECIALTY_STATUS'], specialtyGate),
    () => checkSpecialtyStatusTransitions(tasks));

  // Rule S9: Completed specialty tasks must have actual_minutes
  audit.run(['SPECIALTY_ACTUAL_MINUTES'], gate(['SPECIALTY_ACTUAL_MINUTES'], specialtyGate),
    () => checkSpecialtyActualMinutes(tasks));

  return audit.finish('schedule');
}

/**
 * Rule 1: No overlapping tickets for same tech in same bay
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-BQ Unit 4 — THE BAY THIS RULE MEANS
 * ═══════════════════════════════════════════════════════════════════════════
 * This rule keyed tickets by `ticket.bay_assignment`. That column does not
 * exist. Confirmed against the replayed database: `information_schema.columns`
 * returns zero rows for `tickets.bay_assignment`; the ticket-side bay fields are
 * `bay_id` (uuid, FK to `shop_bays(id)`) and `bay_assignment_source`
 * (provenance, not identity).
 *
 * So on real rows the predicate `t.bay_assignment` excluded every ticket and the
 * rule found nothing, ever. It appeared to work only because test fixtures
 * invented the property — and then reported `bayId: 'Bay 1'`, a display string,
 * as the bay identity. Unit 3's field coverage made the same fact visible from
 * the other side: against real columns this rule reported
 * NOT_EVALUATED_MISSING_FIELD for a column the table has never had.
 *
 * The invariant needs two identities — the technician, and the BAY INSTANCE.
 * `bay_id` is that instance: it is the FK the schema defines and the same
 * identity the `tickets_no_global_bay_overlap` EXCLUDE constraint uses. A human
 * display name is not needed to decide whether two intervals overlap, and
 * `shop_bays.name` carries no uniqueness constraint of any kind — not even per
 * shop — so it could not serve as identity even if it were loaded.
 *
 * Scope is unchanged and stays narrower than BAY_GLOBAL_OVERLAP: this rule is
 * SAME technician in the same bay; that one is ANY technicians in the same bay.
 * The second is a superset at the occupancy level and both are kept, because a
 * named same-tech observation is not made redundant by a broader physical one.
 *
 * Nothing else moved: the time-overlap arithmetic, the status exclusion, the
 * severity and the rule id are all as they were.
 */
function checkNoOverlaps(tickets: any[]): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];
  // DMC-E: removed dead `t.status !== 'Parking Lot'` filter — the
  // tickets_status_check CONSTRAINT (migration 20260424000000) rejects
  // `'Parking Lot'`, so the predicate excluded nothing. Parking-lot
  // tickets carry `parking_lot = true` and have `bay_id = NULL` (see the
  // canonical move_vehicle_location RPC), so they are already excluded
  // by the `bay_id` predicate further down.
  //
  // CSI-J3: same-shape cleanup as CSI-J2's NO_PAST_TIMES casing fix.
  // Pre-J3 this filter read `status !== 'completed'` (lowercase), but
  // the canonical enum (`tickets_status_check` migration 20260424000000)
  // is capitalized `'Completed'`. The bug was symptom-free in production
  // because the trailing predicates are nulled by
  // `mutateTicketTimerState.completeTicket` (sets `assigned_tech_id: null`
  // + `bay_id: null`), so Completed tickets already fell out via those
  // gates. Casing still required for source truth + grep-ability +
  // drift-detector consistency with CSI-J2.
  //
  // `!= null` on the two identities, mirroring the `IS NOT NULL` the
  // database uses for the same columns.
  const activeTickets = tickets.filter(t =>
    t.status !== 'Completed' &&
    t.assigned_tech_id != null &&
    t.bay_id != null &&
    t.start_time &&
    t.opti_finish
  );

  // Group by tech + bay INSTANCE.
  const groups = new Map<string, any[]>();
  activeTickets.forEach(ticket => {
    const key = `${ticket.assigned_tech_id}:${ticket.bay_id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(ticket);
  });

  // Check each group for overlaps
  groups.forEach((groupTickets) => {
    const sorted = groupTickets.sort((a, b) =>
      new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );

    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i];
      const next = sorted[i + 1];

      const currentEnd = new Date(current.opti_finish).getTime();
      const nextStart = new Date(next.start_time).getTime();

      if (currentEnd > nextStart) {
        violations.push({
          rule: 'NO_OVERLAPS',
          severity: 'error',
          // The bay is named by its identity. Resolving a friendly name would
          // mean loading `shop_bays` from inside the invariant engine, and this
          // checker is synchronous and local by design.
          message: `Ticket ${next.id} overlaps with ${current.id} for tech ${current.assigned_tech_id} in bay ${current.bay_id}`,
          ticketId: next.id,
          techId: current.assigned_tech_id,
          bayId: current.bay_id,
          details: { currentEnd, nextStart, overlap: currentEnd - nextStart },
        });
      }
    }
  });

  return violations;
}

/**
 * Rule 2: No tickets scheduled in the past
 * Add 5-minute tolerance to prevent false positives during rapid scheduling changes
 */
function checkNoPastStartTimes(tickets: any[], shopTimezone?: string): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];
  const now = clock.now();
  const toleranceMs = 5 * 60 * 1000; // 5 minutes
  const toleranceThreshold = new Date(now.getTime() - toleranceMs);

  // Compute "today" in shop TZ so advisors viewing from a different browser
  // timezone still classify stale vs past-by-minutes correctly. Falls back to
  // browser-local only when shopTimezone is not available.
  const shopTodayLabel = shopTimezone ? getTodayInShopTz(shopTimezone) : null;

  tickets.forEach(ticket => {
    // CSI-J2: canonical status casing. Pre-CSI-J2 the early-return read
    // `status === 'completed'` (lowercase), but the canonical enum
    // enforced by `tickets_status_check` (migration 20260424000000) is
    // capitalized `'Completed'`. The lowercase predicate never matched
    // production rows, so post-completion tickets retaining `start_time`
    // (the canonical `completeTicket` transition does NOT null
    // start_time) emitted NO_PAST_TIMES `warning` advisories on every
    // checkScheduleInvariants run. Fix: match the canonical capitalized
    // value. Mirrors the DMC-E precedent that removed the same dead
    // lowercase pattern (`status !== 'Parking Lot'`) in this file.
    //
    // NEEDS-FOLLOW-UP (deferred to CSI-J3 candidate): `checkNoOverlaps`
    // on line ~143 has the same lowercase `'completed'` filter shape;
    // its bug is masked because the other predicates
    // (`assigned_tech_id && bay_assignment`) are nulled by the canonical
    // complete transition, so Completed tickets fall out anyway.
    // Symptom-free in production but doctrinally identical.
    //
    // CORE-BQ Unit 9: CSI-J2 fixed ONE THIRD of this predicate.
    //
    // Its reasoning was right and its scope was too narrow. It observed that
    // the canonical complete transition does not null `start_time`, so
    // post-completion rows kept emitting advisories, and corrected the casing.
    // The identical argument covers Cancelled and Deleted, and could not have
    // been made at the time: their single canonical writer
    // (`terminalize_noncompletion_locked`) landed afterwards, and it does not
    // null `start_time` either.
    //
    // It CANNOT. `tickets.start_time` is declared NOT NULL — executed against
    // the live schema, not read from a migration. So every Cancelled and every
    // Deleted row necessarily still carries a start time, which is necessarily
    // in the past, and this rule reported every one of them. The live CHECKs
    // accept such rows; the Unit 9 representability oracle inserts them.
    //
    // Severity made it worse rather than better. A visit cancelled TODAY has
    // `scheduled_date` = today, so it is not "stale", so it was reported at
    // `error` — the sharpest signal the auditor has, spent on a car that is
    // gone. Older ones were reported as warnings "awaiting automatic cleanup",
    // which named a cleanup that will never come for them: the stale-ticket job
    // only nulls `start_time` on its move-to-parking branch, which a terminal
    // row does not take.
    if (!ticket.start_time || !isAdvisableTicket(ticket)) return;

    const startTime = new Date(ticket.start_time);

    // Only flag if more than 5 minutes in the past
    if (startTime < toleranceThreshold) {
      // Check if this is a stale ticket from a previous day
      let isStaleTicket: boolean;
      if (shopTodayLabel && ticket.scheduled_date) {
        // Pure string comparison against shop-local today — no Date round-trip.
        isStaleTicket = ticket.scheduled_date < shopTodayLabel;
      } else {
        // Legacy fallback: browser-local midnight comparison.
        const today = new Date(now);
        today.setHours(0, 0, 0, 0);
        const scheduledDate = ticket.scheduled_date ? parseISO(ticket.scheduled_date + 'T00:00:00') : null;
        isStaleTicket = !!(scheduledDate && scheduledDate < today);
      }
      
      violations.push({
        rule: 'NO_PAST_TIMES',
        severity: isStaleTicket ? 'warning' : 'error', // Downgrade stale tickets to warnings
        message: isStaleTicket 
          ? `Stale ticket ${ticket.id} from ${ticket.scheduled_date} - awaiting automatic cleanup`
          : `Ticket ${ticket.id} has start time in the past`,
        ticketId: ticket.id,
        details: { 
          startTime: startTime.toISOString(), 
          now: now.toISOString(),
          isStaleTicket,
          scheduledDate: ticket.scheduled_date,
        },
      });
    }
  });

  return violations;
}

/**
 * Rule 3: Waiter tickets must finish before the assigned tech's
 * shift end (i.e. before the tech leaves for the day).
 *
 * CSI-J1 (2026-05-03) — JS-correction round.
 * --------------------------------------------------------------------
 * Pre-CSI-J1 this rule was DOUBLE-BROKEN and silently never fired:
 *
 *   1. NON-EXISTENT COLUMN. The original code read `tech.clock_out`,
 *      but the `technicians` schema has no `clock_out` column. The
 *      canonical end-of-shift fields are `tech.shift_end` (legacy
 *      flat HH:MM) and `tech.hours[day].close` (canonical per-day
 *      HH:MM, post CSI-S2/S3/S4). The early-return at
 *      `if (!tech?.clock_out) return;` therefore tripped on every
 *      ticket → no violations were ever recorded.
 *
 *   2. ISO TIMESTAMP PASSED TO `parseTime()`. Even if (1) had been
 *      satisfied, the comparison `parseTime(ticket.opti_finish)`
 *      passed a UTC ISO timestamp like `'2025-03-15T14:00:00.000Z'`
 *      to a helper that splits HH:MM strings on `:` — `Number('2025-
 *      03-15T14') === NaN`. `NaN > anything === false`, so even past
 *      the early-return the comparison would never have triggered.
 *
 *   The dead-line `closeMinutes = parseTime(shopProfile.close_time)`
 *   was also pure dead code — the variable was computed but never
 *   read in the comparison.
 *
 * CSI-J1 fix (smallest correction; mirrors Rule S6's DTC-C pattern):
 *   • Add `shopTimezone?: string` parameter, threaded from
 *     `checkScheduleInvariants(...)` via `shopProfile?.time_zone`.
 *   • Convert `opti_finish` from UTC ISO to minutes-since-shop-local-
 *     midnight via the canonical `getMinutesInShopTZ()` helper. Falls
 *     back to browser-local `Date#getHours()` math when shopTimezone
 *     is missing — same dual-path structure DTC-C established for
 *     SPECIALTY_TECH_SHIFT.
 *   • Switch end-of-shift source from non-existent `tech.clock_out`
 *     to canonical-first `getTechDayHours(tech, dateStr)?.close` with
 *     legacy fallback to `tech.shift_end`. Mirrors the CSI-S2 pattern
 *     applied across `useAutoBump` / `eodRolloverHandler` / etc.
 *   • Drop the dead `closeMinutes` line + the `if (!shopProfile?.
 *     close_time)` early-return that gated on it.
 *
 * Severity unchanged at `error` per CSI-J1 directive ("Preserve
 * existing invariant severity unless there is a clear reason to
 * change it"). The rule was always SUPPOSED to fire as `error`; the
 * fix is restoring intended behavior, not tightening a soft rule.
 *
 * NOTE — operational expectation: this rule now fires for the first
 * time. Production data may show pre-existing violations that have
 * been silently invisible until now (Waiter tickets whose `opti_finish`
 * exceeds the assigned tech's `shift_end` because operators legitimately
 * extended the day). These show up as `checkScheduleInvariants` console
 * output via the existing `OptiPlaceModal` integration — no write
 * blocking. A future DB-trigger-class round (out of CSI-J scope) would
 * need a live-audit + soft-warn-vs-hard-block policy decision before
 * promoting to atomic refusal.
 */
function checkWaiterEOD(
  tickets: any[],
  technicians: any[],
  shopProfile: any,
  shopTimezone?: string,
): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];

  tickets.forEach(ticket => {
    // Canonical column is `type` ('Waiter' | 'Drop-off'); the prior
    // `ticket.ticket_type` lookup always read undefined (the field is not
    // populated by app writers and is not in the supabase types), so this
    // invariant silently never fired. Use `type` so the Waiter EOD check
    // actually runs.
    //
    // CORE-BQ Unit 9: this rule had NO lifecycle filter of any kind.
    //
    // Not a too-narrow one — none. NO_PAST_TIMES at least exempted `Completed`;
    // this exempted nothing, so a finished visit whose `opti_finish` ran past
    // its technician's shift end was reported as a live promise breach on every
    // audit, permanently. The promise it describes was either kept or broken
    // hours ago, and either way is no longer actionable.
    //
    // How much of that reaches production depends on writers this environment
    // cannot execute — neither `complete_ticket_with_location` nor
    // `terminalize_noncompletion_locked` is deployed in the local replay, so
    // which companion columns each clears is PROVENANCE here, not proof. On
    // migration text: completion and cancellation both release
    // `assigned_tech_id` (so those rows fall out of the guard below by
    // accident), while deletion deliberately preserves it "so restore has
    // something to restore to" — leaving Deleted as the class that reaches this
    // rule with everything it reads still populated.
    //
    // The repair does not rest on that. Terminal Waiter rows carrying
    // `opti_finish` AND `assigned_tech_id` are accepted by every live CHECK —
    // executed, not argued — so the rule must not speak about them regardless
    // of how often a writer produces one. Depending on a companion column being
    // incidentally null is not a lifecycle filter; it is the absence of one.
    if (!isAdvisableTicket(ticket)) return;
    if (ticket.type !== 'Waiter' || !ticket.opti_finish || !ticket.assigned_tech_id) return;

    const tech = technicians.find(t => t.id === ticket.assigned_tech_id);
    // Unit 9 leaves this silent skip in place, and says so rather than hiding
    // it. A missing technician means this ticket was NOT evaluated, which is
    // not the same as evaluating it and finding nothing — the exact distinction
    // Unit 6 exists to keep. Reporting it honestly requires a per-row
    // not-evaluated channel the violation contract does not have, so the honest
    // move available here is to refuse the stronger row-scope claim: the
    // technicians dataset stays FILTERED for this rule at every caller, and the
    // Unit 9 record carries this as an open population gap rather than a fixed
    // one.
    if (!tech) return;

    // Convert opti_finish (UTC ISO) to minutes-since-shop-local-midnight.
    // DTC-C dual-path: canonical via getMinutesInShopTZ when shopTimezone
    // is available; legacy browser-local Date math when it isn't.
    let finishMinutes: number;
    if (shopTimezone) {
      finishMinutes = getMinutesInShopTZ(ticket.opti_finish, shopTimezone);
    } else {
      const finish = new Date(ticket.opti_finish);
      finishMinutes = finish.getHours() * 60 + finish.getMinutes();
    }

    // CSI-S2 dual-path: read canonical per-day shift end via
    // getTechDayHours when shopTimezone lets us derive the shop-local
    // board date; fall back to legacy `tech.shift_end` flat column when
    // either timezone is missing or per-day hours aren't configured.
    const ticketDateStr = shopTimezone
      ? getShopDayLabel(ticket.opti_finish, shopTimezone)
      : null;
    const dayHours = ticketDateStr ? getTechDayHours(tech, ticketDateStr) : null;
    const canonicalShiftEnd = dayHours?.close || tech.shift_end;
    if (!canonicalShiftEnd) return;

    const techShiftEndMinutes = parseTime(canonicalShiftEnd);

    if (finishMinutes > techShiftEndMinutes) {
      violations.push({
        rule: 'WAITER_EOD',
        severity: 'error',
        message: `Waiter ticket ${ticket.id} finishes after tech ${tech.name}'s shift end`,
        ticketId: ticket.id,
        techId: tech.id,
        details: { finish: ticket.opti_finish, shiftEnd: canonicalShiftEnd },
      });
    }
  });

  return violations;
}

/**
 * Rule 4: Parking-lot tickets cannot be auto-assigned to a technician.
 *
 * DMC-E: pre-D this rule branched on `ticket.status === 'Parking Lot'`,
 * which never matched (the tickets_status_check CONSTRAINT in
 * migration 20260424000000 rejects that value). The intended invariant
 * was therefore silently un-enforced: a parking-lot ticket retaining
 * an `assigned_tech_id` would produce no violation. The canonical
 * parking-lot SoT is `tickets.parking_lot = true`; the helper
 * centralizes the check. The trailing `status !== 'completed'`
 * predicate from the prior implementation is preserved verbatim — it
 * carved out completed-but-still-parked tickets from the warning.
 */
/**
 * The statuses the database's `tickets_parking_lot_immunity` treats as exempt.
 *
 * Both casings, because the live constraint carries both:
 *   `status = ANY (ARRAY['completed'::text, 'Completed'::text])`
 *
 * Only `'Completed'` is a valid full-row state — `tickets_status_check` limits
 * status to Active/Paused/Completed/Blocked/Deleted/Cancelled, so a lowercase
 * row cannot exist in `public.tickets`. The lowercase entry is therefore
 * constraint-level compatibility, not a production status, and it is mirrored
 * here so the two rules answer identically for every state the named constraint
 * can be asked about.
 *
 * This is deliberately NOT a general status normalisation. It is one named
 * rule's exemption list.
 */
const PARKING_LOT_IMMUNITY_EXEMPT_STATUSES = new Set(['completed', 'Completed']);

/**
 * Does the database intentionally permit this parked+assigned ticket?
 *
 * Mirrors the three exemptions in the live constraint, and nothing else.
 *
 * COMPLETED — the completing technician is retained on a still-parked ticket
 * for forensics. The constraint has carried this exemption since it was added.
 *
 * ROLLOVER CONTINUATION — an EOD rollover Part B is a FUTURE-STATE PLAN. When a
 * placement cannot finish before close, the create flow inserts a next-day
 * continuation that intentionally carries `parking_lot = true`, `bay_id`,
 * `assigned_tech_id` and `status = 'Active'` together, describing where the work
 * WILL be rather than where the vehicle IS. Before the exemption existed every
 * EOD rollover insert failed with 23514. Only rollover children set
 * `rollover_of_ticket_id`; ordinary tickets and ticket-splits leave it null.
 *
 * `!= null` rather than a truthiness test, so this reads NULL/non-NULL the way
 * `IS NOT NULL` does.
 */
function isParkingLotImmunityExempt(ticket: any): boolean {
  if (PARKING_LOT_IMMUNITY_EXEMPT_STATUSES.has(ticket?.status)) return true;
  if (ticket?.rollover_of_ticket_id != null) return true;
  // ROLLOVER CHAIN HEAD (20270201140000) — the head of a live chain, parked
  // while keeping its planned technician. Server-computed; never client-set.
  if (ticket?.parked_with_chain_plan === true) return true;
  return false;
}

/**
 * Rule 4: a parked ticket must not also be assigned to a technician.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-BQ Unit 2 — THIS RULE NOW SAYS WHAT THE DATABASE SAYS
 * ═══════════════════════════════════════════════════════════════════════════
 * The lower layer already prevents this state. `tickets_parking_lot_immunity`
 * is the authority, and the auditor's job is to report the same invariant, not
 * a stricter one — an auditor that calls a state impossible while the database
 * deliberately permits it is lying about the operational model.
 *
 * It was doing exactly that. Measured against the live constraint over a
 * seventeen-state matrix: FOUR false positives, zero false negatives.
 *
 *   a completed-but-parked ticket keeping its technician for forensics;
 *   an EOD rollover continuation, in three of its states.
 *
 * The two causes were an absent rollover exemption, and a status test written
 * as `status !== 'completed'` — lowercase, while the canonical enum is
 * capitalised, so the completed exemption never matched a real row at all.
 *
 * Equivalence is now proved by EXECUTING both sides over one shared matrix
 * rather than by comparing source text; see
 * `tests/setup/parkingImmunityOracle.ts`. The previous drift tests compared
 * spellings and passed throughout the divergence.
 *
 * The direction of the old error is worth keeping in mind: the auditor was
 * noisier than the database, never more permissive. Nothing unsafe was
 * writable; the audit was reporting legal states as contradictions.
 */
function checkParkingLotImmunity(tickets: any[]): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];

  tickets.forEach(ticket => {
    if (
      isInParkingLot(ticket)
      && ticket.assigned_tech_id != null
      && !isParkingLotImmunityExempt(ticket)
    ) {
      violations.push({
        rule: 'PARKING_LOT_IMMUNITY',
        severity: 'warning',
        message: `Parking Lot ticket ${ticket.id} should not be auto-assigned`,
        ticketId: ticket.id,
        techId: ticket.assigned_tech_id,
      });
    }
  });

  return violations;
}

/**
 * Rule 5: Lift-required jobs cannot be in flat-only bays (hard constraint).
 * NOTE: The reverse is allowed — flat-OK jobs CAN be placed in lift bays (lift ⊇ flat).
 * Technician concurrency is also allowed (multi-bay); we do NOT enforce one-ticket-per-tech.
 *
 * CSI-I3-A4 SAFE — INTENTIONAL observability-only invariant.
 * ----------------------------------------------------------
 * This rule is NOT the canonical lift-safety enforcement layer in production.
 * It is preserved as JS observability ONLY, for the reasons documented below.
 *
 * 1. The driver column `tickets.requires_lift` is structurally vestigial.
 *    Repo-wide grep (CSI-I3-A3, 2026-05-03) confirms exactly 3 occurrences
 *    of `requires_lift` across 2 files: 2 inside this rule, plus 1 doc
 *    reference in `tests/BUNDLE_H_COMPLETE.md`. ZERO writers exist in
 *    `src/` or `supabase/functions/` or `supabase/migrations/`. No
 *    production code path populates `requires_lift`, so the predicate
 *    `ticket.requires_lift && bay.bay_type === 'flat'` cannot fire on
 *    real rows — the rule has been silently advisory since at least the
 *    canonical-lift-safety migration below was added.
 *
 * 2. The CANONICAL lift-safety enforcement is the `move_vehicle_location`
 *    RPC at `supabase/migrations/20260424010000_move_vehicle_location_locked_out_guard.sql`
 *    (and its predecessor `20260421230000_harden_parking_occupancy.sql`).
 *    The RPC compares `tickets.gvwr_lbs` (vehicle Gross Vehicle Weight
 *    Rating) against `shop_bays.lift_capacity_lbs` and refuses the move
 *    when GVWR exceeds the bay's rated capacity. On rejection it also
 *    returns a `compatibleLifts` array — the set of bays in the same
 *    shop with sufficient capacity, sorted ascending — so the UI can
 *    suggest a viable alternative. This is the operationally-correct
 *    lift-safety model: vehicles are lift-incompatible by their physical
 *    weight, not by an abstract `requires_lift` boolean.
 *
 * 3. CSI-I3-A4 (audit + policy round CSI-I3-A3) confirmed:
 *    - No DB trigger / CHECK constraint should be added for this rule.
 *    - Routing this through `mutateTicketSchedule` is structurally
 *      irrelevant — `bay_assignment` and `requires_lift` are not in
 *      `ALLOWED_SCHEDULE_FIELDS` and the rule isn't a write-time
 *      constraint anyway.
 *    - The rule remains here as defense-in-depth observability for the
 *      hypothetical future state where `requires_lift` becomes populated
 *      AND operators want a JS-side advisory in addition to the
 *      GVWR-based RPC enforcement.
 *
 * Audit verdict: CSI-I3-A4 SAFE / INTENTIONAL — observability-only,
 * canonical enforcement is GVWR vs lift_capacity_lbs in
 * move_vehicle_location.
 */
function checkBayTypeRequirements(tickets: any[], bays: any[]): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];

  // CORE-BQ Unit 4: mechanical identity correction only.
  //
  // This looked the bay up by `ticket.bay_assignment`, a column the tickets
  // table does not have, so the rule could never fire on real rows for two
  // independent reasons. The identity is now the canonical `bay_id` FK, matching
  // NO_OVERLAPS and BAY_GLOBAL_OVERLAP.
  //
  // The OTHER reason is unchanged and is the one that matters: `requires_lift`
  // has zero production writers, so this rule remains the observability-only
  // advisory the CSI-I3-A4 SAFE verdict describes, and canonical lift safety
  // remains GVWR vs lift_capacity_lbs in move_vehicle_location. Correcting the
  // identity does not wake the rule up, and is not lift-safety work.
  tickets.forEach(ticket => {
    if (ticket.bay_id == null || !ticket.requires_lift) return;

    const bay = bays.find(b => b.id === ticket.bay_id);
    if (!bay) return;

    if (bay.bay_type === 'flat' && ticket.requires_lift) {
      violations.push({
        rule: 'BAY_TYPE_REQUIREMENT',
        severity: 'error',
        message: `Lift-required ticket ${ticket.id} placed in flat bay ${bay.name}`,
        ticketId: ticket.id,
        bayId: bay.id,
        details: { requires: 'lift', bayType: 'flat' },
      });
    }
  });

  return violations;
}

/**
 * Compare two label lists as MULTISETS.
 *
 * Order-independent, duplicate-sensitive. The canonical writer builds the
 * technician array with `array_agg(...)` carrying neither `ORDER BY` nor
 * `DISTINCT`, so order is not guaranteed and multiplicity is meaningful: a
 * technician holding two flat bays legitimately has `['Flat', 'Flat']`, and
 * collapsing that to a Set would call it equal to one flat bay.
 *
 * Neither input is mutated — the caller's arrays are the audit's evidence.
 */
function sameLabelMultiset(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const counts = new Map<string, number>();
  for (const label of a) counts.set(label, (counts.get(label) ?? 0) + 1);
  for (const label of b) {
    const remaining = counts.get(label);
    if (!remaining) return false;
    counts.set(label, remaining - 1);
  }
  return true;
}

/** What is in `expected` that `actual` lacks, counting duplicates. */
function labelMultisetDifference(
  expected: readonly string[], actual: readonly string[],
): string[] {
  const counts = new Map<string, number>();
  for (const label of actual) counts.set(label, (counts.get(label) ?? 0) + 1);
  const missing: string[] = [];
  for (const label of expected) {
    const remaining = counts.get(label) ?? 0;
    if (remaining > 0) counts.set(label, remaining - 1);
    else missing.push(label);
  }
  return missing;
}

/**
 * Rule 6: the technician's derived bay-type projection agrees with the bays
 * actually assigned to them.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-BQ Unit 4 — WHAT THESE TWO COLUMNS ACTUALLY HOLD
 * ═══════════════════════════════════════════════════════════════════════════
 * This rule compared `technicians.bay_assignments[]` against `shop_bays.name`,
 * as though the array held bay INSTANCE names. It does not, and the canonical
 * writer says so plainly. `sync_tech_bay_assignments(p_tech_id, p_bay_ids,
 * p_shop_id)` takes bay UUIDs, writes the instance assignment to
 * `shop_bays.assigned_tech_id`, and then REBUILDS the technician array from
 * `shop_bays.bay_type` through a display-label CASE:
 *
 *   2-post -> '2-Post Lift'   ·  alignment-adas -> 'Alignment + ADAS'
 *   flat   -> 'Flat'          ·  general        -> 'Flat'
 *
 * So the array is a denormalised TYPE projection. It carries no instance
 * identity at all, and `shop_bays.name` ('Bay 1') will essentially never appear
 * in it. Run against real canonical output the old rule reported two violations
 * per technician — "assigned to non-existent bay 2-Post Lift" and "Bay 1 not in
 * tech's bay_assignments" — both false, and both describing the wrong thing.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE INVARIANT THESE TWO REPRESENTATIONS CAN ACTUALLY SUPPORT
 * ═══════════════════════════════════════════════════════════════════════════
 * `shop_bays.assigned_tech_id` is the source of truth for which bay INSTANCES
 * belong to a technician; `technicians.bay_assignments` is derived from it. The
 * only honest thing to audit is therefore whether the derivation still holds:
 *
 *   multiset(tech.bay_assignments)
 *     == multiset(bayTypeToLabel(bay.bay_type) for bays assigned to that tech)
 *
 * On mismatch the derived projection is what is wrong, and the rule says so.
 * It never concludes that `shop_bays` should be rewritten from the labels, and
 * it repairs nothing — CORE-BQ forbids silent repair.
 *
 * `bayTypeToLabel` is the same mapping the live writer uses; all eleven enum
 * values were compared against the live `pg_get_functiondef` output and agree.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * A SCOPE LIMIT, STATED RATHER THAN GUESSED
 * ═══════════════════════════════════════════════════════════════════════════
 * The old rule also walked the bays and reported "bay assigned to non-existent
 * tech" whenever `assigned_tech_id` was not in the supplied technician array.
 * Nothing tells this engine that the array is a complete shop roster, and the
 * one production caller loads a SUBSET — the technicians of the tickets it
 * scanned. That direction therefore concluded "this technician does not exist"
 * from "this technician was not passed in", which is a different statement.
 *
 * It is gone. The rule evaluates the technicians it was actually given. Proving
 * the reverse direction needs row-scope completeness the input model does not
 * yet carry, and inventing it here would be a bigger change than this unit.
 *
 * ===========================================================================
 * CSI-I3-A5 SAFE — the prior verdict, preserved and updated
 * ===========================================================================
 * The audit round CSI-I3-A3 (2026-05-03) already reached the COLUMN-
 * REPRESENTATION MISMATCH finding recorded below, and closed it as CSI-I3-A5
 * SAFE / INTENTIONAL — observability-only. That record is kept because its
 * canonical-ownership and no-trigger conclusions are still binding. Only the
 * part CORE-BQ Unit 4 actually changed is re-stated.
 *
 * 1. COLUMN-REPRESENTATION MISMATCH — RESOLVED BY CORE-BQ UNIT 4.
 *    CSI-I3-A3 found that this rule compared `tech.bay_assignments[i]`, a
 *    bay-TYPE display string, against `bay.name`, the bay-INSTANCE name
 *    ('Bay 1', 'Bay 2'). Instance names essentially never equal type labels,
 *    so the rule had been silently advisory by representation drift since the
 *    RPC was added. That round left the fix for later, naming it exactly:
 *    "compare `tech.bay_assignments[i]` against the bay's display-type string
 *    instead of its name". Unit 4 is that fix, as a multiset comparison.
 *
 * 2. CANONICAL ENFORCEMENT LAYER — the `sync_tech_bay_assignments` RPC.
 *    Introduced as the atomic 5-step transaction in
 *    `20260421235000_atomic_sync_tech_bay_assignments.sql`: auth via
 *    `get_user_shop_role`, cross-shop validation, clear this tech from all
 *    bays in the shop, assign the selected bays, rebuild the denormalised
 *    display-string array, write it to `technicians.bay_assignments`. One
 *    PL/pgSQL transaction; any step throwing rolls back the rest. The TS
 *    helper `syncBayAssignments` in `src/utils/bayAssignmentValidation.ts`
 *    is a thin delegate — it performs no writes itself.
 *
 *    CORE-BQ Unit 4 CORRECTION. That migration is SUPERSEDED. The current
 *    definition is `20260601130000_fix_sync_tech_bay_assignments_enum_cast.sql`,
 *    and the CSI-I3-A3 record of the CASE was accurate for 2026-04-21 but is
 *    stale now: the superseded version had eight arms and mapped
 *    'wash' -> 'Wash Bay' and 'alignment-adas' -> 'Alignment & ADAS'. The live
 *    function has eleven and maps:
 *      '2-post'         → '2-Post Lift'    'adas'        → 'ADAS'
 *      '4-post'         → '4-Post Lift'    'wash'        → 'Wash'
 *      'scissor'        → 'Scissor Lift'   'inspection'  → 'Inspection'
 *      'single-post'    → 'Single-Post Lift'
 *      'alignment'      → 'Alignment'      'flat'        → 'Flat'
 *      'alignment-adas' → 'Alignment + ADAS'
 *      'general'        → 'Flat'
 *    This listing is documentation, not a second implementation: the rule
 *    calls `bayTypeToLabel`, and the round-trip suite proves that helper
 *    agrees with the live writer for every one of the eleven enum values by
 *    executing the RPC, rather than by reading either mapping.
 *
 * 3. CSI-I3-A5 confirmed, and Unit 4 does not disturb:
 *    - No DB trigger / CHECK constraint should be added for this rule. A
 *      bidirectional trigger pair (`AFTER UPDATE OF assigned_tech_id ON
 *      shop_bays` + `AFTER UPDATE OF bay_assignments ON technicians`) would
 *      invert the canonical RPC ownership: the RPC owns atomic writes to both
 *      columns, so layering triggers on top opens lock-ordering and
 *      re-entrancy concerns inside the RPC's own multi-step transaction,
 *      whose clear → assign → array-write steps would each re-fire them.
 *    - Routing this through `mutateTicketSchedule` is structurally
 *      irrelevant — neither column is a ticket column.
 *
 * Audit verdict, restated: CSI-I3-A5 SAFE / INTENTIONAL — observability-only,
 * canonical enforcement remains the `sync_tech_bay_assignments` RPC. Unit 4
 * changed only which two things the observation compares; it adds no trigger,
 * no constraint, and no write path.
 */
function checkBayAssignmentSync(technicians: any[], bays: any[]): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];

  // Bays grouped by the technician they are assigned to — one pass, so the
  // rule stays O(bays + techs) rather than a scan per technician.
  const baysByTech = new Map<string, any[]>();
  bays.forEach(bay => {
    const techId = bay?.assigned_tech_id;
    if (techId == null) return;
    const bucket = baysByTech.get(techId);
    if (bucket) bucket.push(bay);
    else baysByTech.set(techId, [bay]);
  });

  technicians.forEach(tech => {
    if (!tech || tech.id == null) return;

    const expected = (baysByTech.get(tech.id) ?? [])
      .map(bay => bayTypeToLabel(String(bay.bay_type)));
    const actual = Array.isArray(tech.bay_assignments) ? tech.bay_assignments.map(String) : [];

    if (sameLabelMultiset(expected, actual)) return;

    violations.push({
      rule: 'BAY_SYNC',
      severity: 'error',
      message:
        `Technician ${tech.name ?? tech.id} bay_assignments do not match the bay types `
        + `assigned to them (expected ${expected.length}, stored ${actual.length})`,
      techId: tech.id,
      details: {
        // Bay-type display labels and counts. No customer data, no ticket data.
        expectedLabels: [...expected].sort(),
        actualLabels: [...actual].sort(),
        missingLabels: labelMultisetDifference(expected, actual),
        extraLabels: labelMultisetDifference(actual, expected),
      },
    });
  });

  return violations;
}

/**
 * Helper: Parse HH:MM time string to minutes
 */
function parseTime(timeStr: string): number {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * ========== SPECIALTY INVARIANTS ==========
 */

/**
 * Rule S1: Specialty tasks must have valid bay bookings
 */
function checkSpecialtyTaskBookings(specialtyTasks: any[], bayBookings: any[]): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];

  specialtyTasks.forEach(task => {
    const hasBooking = bayBookings.some(booking => booking.specialty_task_id === task.id);
    
    if (!hasBooking && task.status !== 'cancelled') {
      violations.push({
        rule: 'SPECIALTY_TASK_BOOKING',
        severity: 'error',
        message: `Specialty task ${task.id} has no bay booking`,
        details: { taskId: task.id, status: task.status },
      });
    }
  });

  return violations;
}

/**
 * Rule S2: Specialty bays cannot be double-booked
 */
function checkSpecialtyBayDoubleBooking(bayBookings: any[]): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];
  const activeBookings = bayBookings.filter(b => b.status === 'active');

  // Group by bay_id
  const groups = new Map<string, any[]>();
  activeBookings.forEach(booking => {
    if (!groups.has(booking.bay_id)) groups.set(booking.bay_id, []);
    groups.get(booking.bay_id)!.push(booking);
  });

  groups.forEach((bookings, bayId) => {
    const sorted = bookings.sort((a, b) => 
      new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );

    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i];
      const next = sorted[i + 1];
      
      const currentEnd = new Date(current.end_time).getTime();
      const nextStart = new Date(next.start_time).getTime();

      if (currentEnd > nextStart) {
        violations.push({
          rule: 'SPECIALTY_BAY_DOUBLE_BOOKING',
          severity: 'error',
          message: `Specialty bay ${bayId} double-booked`,
          bayId,
          details: { 
            booking1: current.id, 
            booking2: next.id,
            overlap: currentEnd - nextStart 
          },
        });
      }
    }
  });

  return violations;
}

/**
 * Rule S3: Alignment/ADAS tasks must be in appropriate specialty bays
 */
function checkSpecialtyBayTypes(specialtyTasks: any[], bays: any[]): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];

  specialtyTasks.forEach(task => {
    if (!task.bay_id || !task.bay_type) return;

    const bay = bays.find(b => b.id === task.bay_id);
    if (!bay) {
      violations.push({
        rule: 'SPECIALTY_BAY_TYPE',
        severity: 'error',
        message: `Specialty task ${task.id} references non-existent bay ${task.bay_id}`,
        bayId: task.bay_id,
        details: { taskId: task.id },
      });
      return;
    }

    if (bay.bay_category !== 'alignment' && bay.bay_category !== 'adas') {
      violations.push({
        rule: 'SPECIALTY_BAY_TYPE',
        severity: 'error',
        message: `Specialty task ${task.id} in non-specialty bay ${bay.name}`,
        bayId: bay.id,
        details: { 
          taskId: task.id, 
          bayCategory: bay.bay_category,
          requiredCategory: task.bay_type 
        },
      });
    }
  });

  return violations;
}

/**
 * Rule S4: Specialty sub-tickets must reference valid primary tickets
 */
function checkSpecialtySubTicketReferences(specialtyTasks: any[], tickets: any[]): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];

  specialtyTasks.forEach(task => {
    if (!task.primary_ticket_id) {
      violations.push({
        rule: 'SPECIALTY_PRIMARY_REFERENCE',
        severity: 'error',
        message: `Specialty task ${task.id} has no primary ticket reference`,
        details: { taskId: task.id },
      });
      return;
    }

    const primaryTicket = tickets.find(t => t.id === task.primary_ticket_id);
    if (!primaryTicket) {
      violations.push({
        rule: 'SPECIALTY_PRIMARY_REFERENCE',
        severity: 'error',
        message: `Specialty task ${task.id} references non-existent primary ticket ${task.primary_ticket_id}`,
        ticketId: task.primary_ticket_id,
        details: { taskId: task.id },
      });
    }

    if (task.sub_ticket_id) {
      const subTicket = tickets.find(t => t.id === task.sub_ticket_id);
      if (!subTicket) {
        violations.push({
          rule: 'SPECIALTY_SUB_REFERENCE',
          severity: 'error',
          message: `Specialty task ${task.id} references non-existent sub-ticket ${task.sub_ticket_id}`,
          ticketId: task.sub_ticket_id,
          details: { taskId: task.id },
        });
      }
    }
  });

  return violations;
}

/**
 * Rule S5: Specialty tasks must have qualified technicians
 */
function checkSpecialtyTechProficiency(specialtyTasks: any[], technicians: any[], shopProfile: any): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];
  
  const shopCulture = shopProfile?.shop_culture || {};
  const alignmentMinProf = shopCulture.alignment_min_proficiency || 60;
  const adasMinProf = shopCulture.adas_min_proficiency || 70;

  specialtyTasks.forEach(task => {
    if (!task.tech_id) return;

    const tech = technicians.find(t => t.id === task.tech_id);
    if (!tech) {
      violations.push({
        rule: 'SPECIALTY_TECH_PROFICIENCY',
        severity: 'error',
        message: `Specialty task ${task.id} assigned to non-existent tech ${task.tech_id}`,
        techId: task.tech_id,
        details: { taskId: task.id },
      });
      return;
    }

    const proficiencies = tech.proficiencies || {};
    
    if (task.bay_type === 'alignment') {
      const alignProf = proficiencies['alignment'] || 0;
      if (alignProf < alignmentMinProf) {
        violations.push({
          rule: 'SPECIALTY_TECH_PROFICIENCY',
          severity: 'warning',
          message: `Tech ${tech.name} has insufficient alignment proficiency (${alignProf} < ${alignmentMinProf})`,
          techId: tech.id,
          details: { taskId: task.id, proficiency: alignProf, required: alignmentMinProf },
        });
      }
    }

    if (task.bay_type === 'adas') {
      const adasProf = proficiencies['adas'] || 0;
      if (adasProf < adasMinProf) {
        violations.push({
          rule: 'SPECIALTY_TECH_PROFICIENCY',
          severity: 'warning',
          message: `Tech ${tech.name} has insufficient ADAS proficiency (${adasProf} < ${adasMinProf})`,
          techId: tech.id,
          details: { taskId: task.id, proficiency: adasProf, required: adasMinProf },
        });
      }
    }
  });

  return violations;
}

/**
 * Rule S6: Specialty task scheduling must respect technician shifts
 *
 * DTC-C: `task.scheduled_start` / `scheduled_end` are UTC ISO timestamps
 * from the database. Reading `.getHours()` on `new Date(...)` of those
 * returns BROWSER-local hours, which silently mis-compares against
 * `tech.shift_start` (shop-local HH:mm) when the operator's browser
 * timezone differs from the shop's. Route through `getMinutesInShopTZ`
 * so both sides of the comparison are minutes-since-midnight in the
 * shop's wall clock. When `shopTimezone` is missing (legacy callers),
 * fall back to the pre-DTC-C local-Date behavior to preserve output
 * shape — drift only manifests for cross-TZ operators, and a
 * shop-without-timezone is a bigger problem than this rule catches.
 */
function checkSpecialtyTechShifts(
  specialtyTasks: any[],
  technicians: any[],
  shopTimezone?: string,
): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];

  specialtyTasks.forEach(task => {
    if (!task.tech_id || !task.scheduled_start || !task.scheduled_end) return;

    const tech = technicians.find(t => t.id === task.tech_id);
    if (!tech) return;

    let taskStartMin: number;
    let taskEndMin: number;
    if (shopTimezone) {
      // Canonical path: minutes-since-midnight in the shop's local TZ.
      taskStartMin = getMinutesInShopTZ(task.scheduled_start, shopTimezone);
      taskEndMin = getMinutesInShopTZ(task.scheduled_end, shopTimezone);
    } else {
      // Legacy fallback (browser-local Date#getHours). Preserved for
      // callers that have not yet threaded the timezone through.
      const taskStart = new Date(task.scheduled_start);
      const taskEnd = new Date(task.scheduled_end);
      taskStartMin = taskStart.getHours() * 60 + taskStart.getMinutes();
      taskEndMin = taskEnd.getHours() * 60 + taskEnd.getMinutes();
    }

    // CSI-S2: read canonical per-day shift bounds via getTechDayHours()
    // when shopTimezone is available (so we can derive the task's shop-
    // local board date from `task.scheduled_start`). Falls back to the
    // deprecated flat columns when shopTimezone is missing — mirrors the
    // dual-path structure DTC-C established for the task-bounds read.
    const taskBoardDateStr = shopTimezone
      ? getShopDayLabel(task.scheduled_start, shopTimezone)
      : null;
    const dayHours = taskBoardDateStr ? getTechDayHours(tech, taskBoardDateStr) : null;
    const canonicalShiftStart = dayHours?.open || tech.shift_start;
    const canonicalShiftEnd = dayHours?.close || tech.shift_end;
    if (!canonicalShiftStart || !canonicalShiftEnd) return;

    const shiftStartMin = parseTime(canonicalShiftStart);
    const shiftEndMin = parseTime(canonicalShiftEnd);

    if (taskStartMin < shiftStartMin || taskEndMin > shiftEndMin) {
      violations.push({
        rule: 'SPECIALTY_TECH_SHIFT',
        severity: 'error',
        message: `Specialty task ${task.id} scheduled outside tech ${tech.name}'s shift`,
        techId: tech.id,
        details: {
          taskId: task.id,
          taskStart: task.scheduled_start,
          taskEnd: task.scheduled_end,
          shiftStart: canonicalShiftStart,
          shiftEnd: canonicalShiftEnd,
        },
      });
    }
  });

  return violations;
}

/**
 * Rule S7: Specialty bay bookings must not overlap
 */
function checkSpecialtyBookingOverlaps(bayBookings: any[]): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];
  const activeBookings = bayBookings.filter(b => b.status === 'active');

  // Group by tech_id
  const groups = new Map<string, any[]>();
  activeBookings.forEach(booking => {
    if (!booking.tech_id) return;
    if (!groups.has(booking.tech_id)) groups.set(booking.tech_id, []);
    groups.get(booking.tech_id)!.push(booking);
  });

  groups.forEach((bookings, techId) => {
    const sorted = bookings.sort((a, b) => 
      new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );

    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i];
      const next = sorted[i + 1];
      
      const currentEnd = new Date(current.end_time).getTime();
      const nextStart = new Date(next.start_time).getTime();

      if (currentEnd > nextStart) {
        violations.push({
          rule: 'SPECIALTY_BOOKING_OVERLAP',
          severity: 'error',
          message: `Tech ${techId} has overlapping specialty bookings`,
          techId,
          details: { 
            booking1: current.id, 
            booking2: next.id,
            overlap: currentEnd - nextStart 
          },
        });
      }
    }
  });

  return violations;
}

/**
 * Rule S8: Specialty tasks must have valid status transitions
 */
function checkSpecialtyStatusTransitions(specialtyTasks: any[]): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];
  const validStatuses = ['pending', 'active', 'completed', 'cancelled'];

  specialtyTasks.forEach(task => {
    if (!validStatuses.includes(task.status)) {
      violations.push({
        rule: 'SPECIALTY_STATUS',
        severity: 'error',
        message: `Specialty task ${task.id} has invalid status: ${task.status}`,
        details: { taskId: task.id, status: task.status, validStatuses },
      });
    }
  });

  return violations;
}

/**
 * Rule S9: Completed specialty tasks must have actual_minutes
 */
function checkSpecialtyActualMinutes(specialtyTasks: any[]): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];

  specialtyTasks.forEach(task => {
    if (task.status === 'completed' && (!task.actual_minutes || task.actual_minutes <= 0)) {
      violations.push({
        rule: 'SPECIALTY_ACTUAL_MINUTES',
        severity: 'warning',
        message: `Completed specialty task ${task.id} missing actual_minutes`,
        details: { taskId: task.id, actual_minutes: task.actual_minutes },
      });
    }
  });

  return violations;
}

/**
 * ========== LIFECYCLE INVARIANTS (R29) ==========
 *
 * Validates per-ticket lifecycle state consistency independent of scheduling.
 * Canonical facts enforced here:
 *   - Valid statuses: Active | Paused | Completed | Blocked | Deleted | Cancelled
 *     (mirrors the DB-side `tickets_status_check` CHECK constraint installed
 *     by migration 20260424000000 — JS-side enum must stay in lock-step with
 *     the DB-canonical 6-enum or LIFECYCLE_STATUS_ENUM emits false positives
 *     on every canonical Blocked ticket.)
 *   - Valid timer_status: running | paused | stopped
 *   - Paused   ⇒ timer_status='paused' AND pause_reason present
 *   - Active   ⇒ paused_at/pause_reason NOT set
 *   - Completed ⇒ timer_status='stopped' AND bay_id IS NULL AND
 *                 (parking_lot=true OR parking_spot_id OR picked_up_at)
 *   - Cancelled/Deleted ⇒ timer_status='stopped' AND bay_id IS NULL
 *   - picked_up_at present ⇒ bay_id IS NULL AND parking_spot_id IS NULL
 *                             AND parking_lot=false
 *   - Running timer ⇒ status='Active'
 *                     (never Paused/Completed/Blocked/Deleted/Cancelled —
 *                      enforced by LIFECYCLE_RUNNING_STATUS below)
 *
 * Pure helper, zero side effects. Safe to call anywhere for diagnostics.
 */
const VALID_TICKET_STATUSES = new Set(['Active', 'Paused', 'Completed', 'Blocked', 'Deleted', 'Cancelled']);
const VALID_TIMER_STATUSES = new Set(['running', 'paused', 'stopped']);
/**
 * The statuses `tickets_lifecycle_terminal_state` names, in its own order.
 *
 * CORE-BQ Unit 8. Expressed as a set rather than as `status === 'Deleted'`
 * comparisons for two reasons. It mirrors the constraint's
 * `status = ANY (ARRAY['Completed','Cancelled','Deleted'])` shape directly, so
 * the two can be read side by side. And DMC-D3c-2 forbids raw
 * `status === 'Deleted'` tests in this file precisely because they are usually
 * a single-representation DELETED-STATE check that should go through
 * `isTicketDeleted` — this is not one. It asks which terminal set a status
 * belongs to, which is the question the constraint asks.
 */
const TERMINAL_TICKET_STATUSES = new Set(['Completed', 'Cancelled', 'Deleted']);

/**
 * Is a SCHEDULING ADVISORY entitled to speak about this row?
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-BQ Unit 9 — WHY THIS IS THE OPPOSITE CHOICE FROM UNIT 8, ON PURPOSE
 * ═══════════════════════════════════════════════════════════════════════════
 * Unit 8 keyed the lifecycle scan on STATUS ALONE and argued at length against
 * bringing `isTicketDeleted` into it. That argument still holds and this
 * predicate does not weaken it, because the two answer different questions.
 *
 *   A CONSTRAINT MIRROR asks "would the database accept this row?" Its
 *   authority is the deployed CHECK, which names statuses and never mentions
 *   `deleted_at`. Consulting the tombstone there INVENTS violations the
 *   database does not have — which is exactly the false positive Unit 8
 *   removed.
 *
 *   AN ADVISORY asks "is this still work that a human can act on?" No CHECK
 *   enforces `NO_PAST_TIMES` or `WAITER_EOD`; there is no deployed predicate to
 *   mirror. The governing fact is the canonical deleted-state law, and the
 *   canonical Cancelled/Deleted writer tombstones BOTH classes via `deleted_at`
 *   in its own words "because the canonical deleted-state law keys on it and
 *   'Cancelled' is not one of its statuses. Without it a cancelled visit stays
 *   'active' to every consumer." An advisory is such a consumer.
 *
 * So a row is advisable only if it is neither terminal by status nor
 * tombstoned. The tombstone half also covers the state Unit 8 found the
 * database permits and no rule was watching — `deleted_at` set while the status
 * is still operational. Unit 8 correctly declined to call that a LIFECYCLE
 * violation. It is equally correctly not future work.
 *
 * `checkGlobalBayOverlaps` already filters on `!isTicketDeleted(t)`, so this is
 * the established shape for a rule reasoning about live work, not a new one.
 */
function isAdvisableTicket(ticket: { status?: unknown; deleted_at?: unknown }): boolean {
  return !TERMINAL_TICKET_STATUSES.has(ticket.status as string)
    && !isTicketDeleted(ticket as Parameters<typeof isTicketDeleted>[0]);
}

/**
 * CORE-BQ Unit 1: the same honest result contract as the schedule checker.
 *
 * All eleven rules are evaluated in ONE pass over the tickets, so they are
 * recorded as one unit. Splitting them into eleven passes would reorder the
 * emitted violations — a behaviour change — and would report nothing extra,
 * because they share an input and therefore always share a fate.
 *
 * This checker has no production caller today. That is a wiring gap, recorded
 * for a later unit; the result contract has to be honest before anything is
 * built on it.
 */
const LIFECYCLE_RULES: readonly InvariantRuleId[] = [
  'LIFECYCLE_STATUS_ENUM', 'LIFECYCLE_TIMER_ENUM', 'LIFECYCLE_PAUSED_TIMER',
  'LIFECYCLE_PAUSED_REASON', 'LIFECYCLE_ACTIVE_STALE_PAUSE',
  'LIFECYCLE_COMPLETED_TIMER', 'LIFECYCLE_COMPLETED_BAY',
  'LIFECYCLE_TERMINAL_BAY', 'LIFECYCLE_TERMINAL_TIMER',
  'LIFECYCLE_PICKED_UP_LOCATION', 'LIFECYCLE_RUNNING_STATUS',
];

export function checkTicketLifecycleInvariants(
  /** `null`/`undefined` = not loaded. `[]` = loaded, and there are none. */
  tickets: any[] | null | undefined,
  /**
   * CORE-BQ Unit 6. Same contract as the schedule entry point, and the same
   * fail-safe: this checker also reasons over a ticket ARRAY, so it carries
   * exactly the same danger of auditing a subset and calling it complete. The
   * coverage machinery is shared, so the honesty is mechanical rather than a
   * second implementation. It has no production caller to declare anything yet.
   */
  rowScope?: InvariantInputRowScope | null,
): ScheduleInvariantsResult {
  const audit = createInvariantRecorder(rowScope);

  audit.run(
    LIFECYCLE_RULES,
    // The same field honesty. All eleven rules share one pass over the tickets,
    // so they share its column requirements; a partial ticket projection here
    // would produce the same false CHECKED it produced on the schedule side.
    buildGate(
      LIFECYCLE_RULES,
      Array.isArray(tickets) ? {} : { missing: 'tickets_not_loaded' },
      { tickets },
    ),
    () => scanTicketLifecycle(tickets as any[]),
  );

  return audit.finish('lifecycle');
}

/** The rule bodies, unchanged. Extracted only so the recorder can wrap them. */
function scanTicketLifecycle(tickets: any[]): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];

  tickets.forEach(t => {
    if (!t) return;

    // Basic enum validity.
    //
    // CORE-BQ Unit 8: `!= null`, not truthiness. `tickets_status_check` is
    //   CHECK (status = ANY (ARRAY['Active',...]))
    // which rejects '' — but '' is FALSY in JavaScript, so a truthy guard read
    // an invalid value as an absent one and stayed silent where the database
    // refuses the row. The same shape appears three more times below.
    //
    // `status` is NOT NULL in the table, so this exact state cannot reach a
    // real ticket; the correction is fidelity to the predicate rather than a
    // production bug fix. This checker has no production caller yet, so the
    // rows it will eventually be handed are not guaranteed to have come from
    // the database — an auditor that would accept `status: ''` if it ever saw
    // one is the failure mode CORE-BQ responsibility 7 names.
    //
    // `undefined` still falls through: a MISSING property is Unit 3's question,
    // and `undefined != null` is false.
    if (t.status != null && !VALID_TICKET_STATUSES.has(t.status)) {
      violations.push({
        rule: 'LIFECYCLE_STATUS_ENUM',
        severity: 'error',
        message: `Ticket ${t.id} has unknown status: ${t.status}`,
        ticketId: t.id,
      });
    }
    // `tickets_timer_status_check` is
    //   CHECK (timer_status IS NULL OR timer_status = ANY (ARRAY[...]))
    // so NULL is legal and '' is not. `!= null` says exactly that.
    if (t.timer_status != null && !VALID_TIMER_STATUSES.has(t.timer_status)) {
      violations.push({
        rule: 'LIFECYCLE_TIMER_ENUM',
        severity: 'error',
        message: `Ticket ${t.id} has unknown timer_status: ${t.timer_status}`,
        ticketId: t.id,
      });
    }

    // Paused invariants
    if (t.status === 'Paused') {
      // Mirrors `timer_status IS NOT NULL AND timer_status <> 'paused'` in
      // tickets_lifecycle_active_paused_pickup_state.
      if (t.timer_status != null && t.timer_status !== 'paused') {
        violations.push({
          rule: 'LIFECYCLE_PAUSED_TIMER',
          severity: 'error',
          message: `Paused ticket ${t.id} has timer_status=${t.timer_status}`,
          ticketId: t.id,
        });
      }
      if (!t.pause_reason) {
        violations.push({
          rule: 'LIFECYCLE_PAUSED_REASON',
          severity: 'warning',
          message: `Paused ticket ${t.id} missing pause_reason`,
          ticketId: t.id,
        });
      }
    }

    // Active invariants — should not carry pause metadata
    if (t.status === 'Active' && (t.paused_at || t.pause_reason)) {
      violations.push({
        rule: 'LIFECYCLE_ACTIVE_STALE_PAUSE',
        severity: 'warning',
        message: `Active ticket ${t.id} still carries pause metadata`,
        ticketId: t.id,
        details: { paused_at: t.paused_at, pause_reason: t.pause_reason },
      });
    }

    // Completed invariants
    if (t.status === 'Completed') {
      if (t.timer_status === 'running' || t.timer_status === 'paused') {
        violations.push({
          rule: 'LIFECYCLE_COMPLETED_TIMER',
          severity: 'error',
          message: `Completed ticket ${t.id} has timer_status=${t.timer_status}`,
          ticketId: t.id,
        });
      }
      if (t.bay_id) {
        violations.push({
          rule: 'LIFECYCLE_COMPLETED_BAY',
          severity: 'error',
          message: `Completed ticket ${t.id} still has bay_id=${t.bay_id}`,
          ticketId: t.id,
          bayId: t.bay_id,
        });
      }
    }

    // Cancelled / Deleted invariants — no physical occupancy allowed.
    //
    // ═══════════════════════════════════════════════════════════════════════
    // CORE-BQ Unit 8 — WHY THIS KEYS ON STATUS AND NOT ON isTicketDeleted
    // ═══════════════════════════════════════════════════════════════════════
    // This branch used `t.status === 'Cancelled' || isTicketDeleted(t)`, and
    // `isTicketDeleted` is `deleted_at != null || status === 'Deleted'`. So a
    // row with `deleted_at` set and a still-operational status was reported as
    // holding a bay or a live timer illegally.
    //
    // The lower-layer authority disagrees, and it is REACHABLE — measured, not
    // argued. `tickets_lifecycle_terminal_state` is
    //   NOT (status = ANY (ARRAY['Completed','Cancelled','Deleted'])
    //        AND bay_id IS NOT NULL) AND NOT (… AND timer_status IN (…))
    // It names STATUS and never mentions `deleted_at`, and the full-row oracle
    // confirms `status='Active'` + `deleted_at` + `bay_id` is accepted by every
    // constraint on the table.
    //
    // Three independent things say status is the intended authority here:
    //
    //   · The canonical soft-delete writer pins `deleted_at` to TRACK `status`
    //     — `mutateTicketTimerState` sets the tombstone whenever the target
    //     status is 'Deleted', never the reverse.
    //   · DMC-D1 backfilled in that same direction: it healed
    //     `status='Deleted' AND deleted_at IS NULL`, and defines that — not the
    //     opposite pairing — as the ghost.
    //   · `tickets_triad_consistency` deliberately EXEMPTS rows with
    //     `deleted_at IS NOT NULL` from the location triad, so the schema
    //     tolerates retained location on a tombstoned row on purpose.
    //
    // So the database is not stale; this rule was overreaching. A tombstone
    // carried by an operational row is still an anomaly, but it is a DIFFERENT
    // one — "deleted_at without terminal status" — and reporting it as
    // LIFECYCLE_TERMINAL_BAY named the wrong contradiction. No rule is invented
    // for it here; it is recorded as an uncovered invariant.
    //
    // The canonical helper is still the right tool for questions about whether
    // a ticket is deleted — `checkGlobalBayOverlaps` uses it, and should. This
    // rule is not asking that question; it is mirroring a named constraint.
    // Completed has its own two rules above, so this branch owns the rest of
    // the terminal set — the same three statuses the constraint names, split
    // only for reporting granularity.
    if (t.status !== 'Completed' && TERMINAL_TICKET_STATUSES.has(t.status)) {
      if (t.bay_id) {
        violations.push({
          rule: 'LIFECYCLE_TERMINAL_BAY',
          severity: 'error',
          message: `${t.status} ticket ${t.id} still has bay_id=${t.bay_id}`,
          ticketId: t.id,
          bayId: t.bay_id,
        });
      }
      if (t.timer_status === 'running' || t.timer_status === 'paused') {
        violations.push({
          rule: 'LIFECYCLE_TERMINAL_TIMER',
          severity: 'error',
          message: `${t.status} ticket ${t.id} has timer_status=${t.timer_status}`,
          ticketId: t.id,
        });
      }
    }

    // picked_up_at ⇒ no location
    if (t.picked_up_at && (t.bay_id || t.parking_spot_id || t.parking_lot)) {
      violations.push({
        rule: 'LIFECYCLE_PICKED_UP_LOCATION',
        severity: 'error',
        message: `Picked-up ticket ${t.id} still carries location (bay=${t.bay_id}, spot=${t.parking_spot_id}, lot=${t.parking_lot})`,
        ticketId: t.id,
      });
    }

    // Running timer must be on an Active ticket
    // Mirrors `status IS NOT NULL AND status <> 'Active'` in the same
    // constraint.
    if (t.timer_status === 'running' && t.status != null && t.status !== 'Active') {
      violations.push({
        rule: 'LIFECYCLE_RUNNING_STATUS',
        severity: 'error',
        message: `Running timer on non-Active ticket ${t.id} (status=${t.status})`,
        ticketId: t.id,
      });
    }
  });

  return violations;
}

/**
 * Rule 1B: no two tickets may occupy the same bay at the same time.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-BQ Unit 5 — THIS RULE IS LIFTED FROM A DATABASE EXCLUSION
 * ═══════════════════════════════════════════════════════════════════════════
 * The lower layer is `tickets_no_global_bay_overlap`, read live from the
 * replayed database:
 *
 *   EXCLUDE USING gist (bay_id WITH =,
 *                       tstzrange(start_time, opti_finish, '[)') WITH &&)
 *   WHERE (bay_id IS NOT NULL AND start_time IS NOT NULL
 *          AND opti_finish IS NOT NULL
 *          AND status <> ALL (ARRAY['completed','Completed'])
 *          AND deleted_at IS NULL)
 *
 * `tests/setup/globalBayOverlapOracle.ts` drives one shared matrix through BOTH
 * that live constraint and this function, so the equivalence claim is
 * behavioural rather than a comparison of two source texts.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT UNIT 5 CORRECTED
 * ═══════════════════════════════════════════════════════════════════════════
 * 1. EMPTY INTERVALS. `tstzrange(t, t, '[)')` is the EMPTY range, and an empty
 *    range overlaps nothing — Postgres accepts a zero-length ticket sitting
 *    inside a long one. This rule compared `current.end > next.start` without
 *    asking whether `next` occupied any time at all, so it reported a conflict
 *    the database permits. Nothing in `tickets` requires
 *    `start_time <= opti_finish`, so such a row is genuinely representable.
 *    An interval with `start >= finish` now participates in nothing.
 *
 * 2. ADJACENT-ONLY ENUMERATION. The scan sorted a bay's tickets by start and
 *    compared each to ONLY its immediate successor. That is enough to answer
 *    "is this board bad?" but not "which records conflict?", and CORE-BQ
 *    responsibility 3 requires the second. Given
 *
 *        A 09:00-12:00   B 09:30-10:00   C 10:30-11:00
 *
 *    A conflicts with both B and C, and B does not conflict with C. The old
 *    scan compared A-B and B-C, reported one violation, and never mentioned
 *    that C is also double-booked against A. A database constraint may stop at
 *    the first conflicting pair because it only has to refuse a write; an
 *    auditor is describing a board that already exists.
 *
 *    This is now a sweep: tickets are walked in start order while a small set
 *    of still-open intervals is kept. Every open interval conflicts with the
 *    current ticket — that is what "still open" means once the list is sorted —
 *    and intervals that have ended are dropped as the sweep passes them. Each
 *    unordered pair is emitted exactly once, in O(n log n + k) for k conflicts.
 *
 * The eligibility filter, the rule id, the severity, the violation shape and
 * the half-open boundary semantics are all unchanged. Touching intervals
 * (A.end == B.start) remain legal on both sides.
 */
function checkGlobalBayOverlaps(tickets: any[]): ScheduleViolation[] {
  const violations: ScheduleViolation[] = [];

  // Mirrors the constraint's WHERE clause.
  //
  // DMC-E: removed dead `t.status !== 'Parking Lot'` filter — the
  // tickets_status_check CONSTRAINT (migration 20260424000000) rejects
  // that status value, so the predicate excluded nothing. Parking-lot
  // tickets carry `parking_lot = true` and have `bay_id = NULL` (per
  // canonical move_vehicle_location RPC), so they're already excluded
  // by the `t.bay_id` predicate further down.
  const activeTickets = tickets.filter(t =>
    t.status !== 'completed' &&
    t.status !== 'Completed' &&
    // DMC-D3c-2: deletion check via canonical helper (both reps).
    !isTicketDeleted(t) &&
    t.bay_id &&  // Use bay_id (UUID) as authoritative key
    t.start_time &&
    t.opti_finish
  );

  // Group by bay_id ONLY (not by tech). The EXCLUDE constraint does not
  // reference `assigned_tech_id`, so neither does this verdict — a tech id is
  // diagnostic context, and a missing one must never hide a physical conflict.
  const bayGroups = new Map<string, any[]>();
  activeTickets.forEach(ticket => {
    const bayId = ticket.bay_id;
    if (!bayGroups.has(bayId)) bayGroups.set(bayId, []);
    bayGroups.get(bayId)!.push(ticket);
  });

  bayGroups.forEach((bayTickets, bayId) => {
    if (bayTickets.length < 2) return;

    // Parse once, and drop intervals that occupy no time. `[start, finish)`
    // with start >= finish is the empty range in Postgres; it overlaps nothing.
    // A reversed interval cannot exist in the table at all — the constraint's
    // own index expression raises before the row lands — so treating it the
    // same way is vacuous rather than lenient.
    const spans = bayTickets
      .map(t => ({ t, start: new Date(t.start_time).getTime(), end: new Date(t.opti_finish).getTime() }))
      .filter(s => Number.isFinite(s.start) && Number.isFinite(s.end) && s.start < s.end)
      .sort((a, b) => a.start - b.start);

    // Sweep. `open` holds spans that started earlier and have not yet ended;
    // sorted order makes "still open" exactly equivalent to "overlaps current".
    const open: typeof spans = [];
    for (const span of spans) {
      let kept = 0;
      for (let i = 0; i < open.length; i++) {
        if (open[i].end > span.start) open[kept++] = open[i];
      }
      open.length = kept;

      for (const earlier of open) {
        const current = earlier.t;   // starts first
        const next = span.t;         // starts later, or ties
        const overlapMinutes = Math.ceil(
          (Math.min(earlier.end, span.end) - span.start) / 60000);
        const sameTech = current.assigned_tech_id === next.assigned_tech_id;

        violations.push({
          rule: 'BAY_GLOBAL_OVERLAP',
          severity: 'error',
          message: sameTech
            ? `Bay ${bayId}: Ticket ${next.id} overlaps with ${current.id} by ${overlapMinutes}min (same tech)`
            : `Bay ${bayId}: Ticket ${next.id} (${next.assigned_tech_id}) starts before ${current.id} (${current.assigned_tech_id}) finishes - overlap ${overlapMinutes}min`,
          ticketId: next.id,
          techId: next.assigned_tech_id,
          bayId: bayId,
          details: {
            currentTicketId: current.id,
            currentTechId: current.assigned_tech_id,
            currentEnd: new Date(current.opti_finish).toISOString(),
            nextTicketId: next.id,
            nextTechId: next.assigned_tech_id,
            nextStart: new Date(next.start_time).toISOString(),
            overlapMinutes,
            sameTech,
          },
        });
      }

      open.push(span);
    }
  });

  return violations;
}
